import classnames from 'classnames';
import { components, type ActionMeta, type InputActionMeta, type MenuListProps } from 'react-select';

import '@components/Select/MultiSelect/MultiSelect_.scss';

import { Icon, IconType } from '@components/Icon';
import { normalizeYoFilter } from '@utils/normalizeYoString';
import { OptionType } from '@components/Select';

const NEW_ITEM_FLAG = '__isNew__';

export const MenuList = (props: MenuListProps<OptionType, true>) => {
    const selectProps = props.selectProps as {
        menuStyle?: Record<string, string>;
        inputValue?: string;
        value?: OptionType[];
        onChange?: (newValue: OptionType[] | null, actionMeta: ActionMeta<OptionType>) => void;
        onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
    };
    const { menuStyle = {}, inputValue } = selectProps;
    const visibleOptions = props.options.filter(option => normalizeYoFilter(option as OptionType, inputValue));

    if (visibleOptions.length === 0) {
        return (
            <components.MenuList {...props}>
                <div style={menuStyle}>{props.children}</div>
            </components.MenuList>
        );
    }

    return (
        <components.MenuList {...props}>
            <div style={menuStyle}>
                <div className={'component_multi_select__container'}>
                    {visibleOptions.map(option => {
                        const optionData = option as OptionType & Record<string, unknown>;
                        const isSelected = selectProps.value?.some(selected => selected.value === optionData.value);
                        const isNewOption = !!optionData[NEW_ITEM_FLAG];
                        const isDisabled = !!optionData.isDisabled;
                        const toggleOption = (): void => {
                            const currentValue = selectProps.value || [];
                            const newValue = isSelected
                                ? currentValue.filter(selected => selected.value !== optionData.value)
                                : [...currentValue, optionData];
                            selectProps.onInputChange?.('', {
                                action: 'set-value',
                                prevInputValue: inputValue ?? '',
                            });
                            selectProps.onChange?.(newValue, {
                                action: 'select-option',
                            } as ActionMeta<OptionType>);
                        };

                        return (
                            <div
                                key={optionData.value}
                                style={{
                                    order: isSelected ? 1 : 3,
                                }}
                                className={classnames([
                                    'component_select__menu_item',
                                    isSelected && 'component_select__menu_item_selected',
                                    isDisabled && 'component_select__menu_item_disabled',
                                    isNewOption && 'component_select__menu_item__withoutPadding',
                                ])}
                                onClick={isDisabled ? undefined : toggleOption}
                            >
                                {isNewOption ? (
                                    <div className={'component_select__create_item'}>{optionData.label}</div>
                                ) : (
                                    <>
                                        <div
                                            className={classnames([
                                                'component_select__checkbox',
                                                isSelected && 'component_select__checkbox_checked',
                                            ])}
                                        >
                                            <div className={'component_select__icon'}>
                                                {isSelected && <Icon type={IconType.CHECK_REDESIGN} />}
                                            </div>
                                        </div>
                                        {optionData.label}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </components.MenuList>
    );
};
